import { ErrorNotification, ErrorNotificationOptions } from '../types/errorNotifications';
import i18n from '../i18n/config';

class ErrorNotificationManager {
  private static instance: ErrorNotificationManager;
  private notifications: ErrorNotification[] = [];
  private listeners: Set<(notifications: ErrorNotification[]) => void> = new Set();
  private zIndexBase = 1500; // Higher than most modals
  private maxNotifications = 5; // Maximum notifications to display at once

  private constructor() {
    this.loadPersistentNotifications();
    this.setupVisibilityHandling();
  }

  static getInstance(): ErrorNotificationManager {
    if (!ErrorNotificationManager.instance) {
      ErrorNotificationManager.instance = new ErrorNotificationManager();
    }
    return ErrorNotificationManager.instance;
  }

  // Subscribe to notification changes
  subscribe(callback: (notifications: ErrorNotification[]) => void): () => void {
    this.listeners.add(callback);
    callback(this.notifications);
    
    return () => {
      this.listeners.delete(callback);
    };
  }

  // Add a new error notification
  addNotification(
    title: string, 
    message: string, 
    options: ErrorNotificationOptions = {}
  ): string {
    const {
      level = 'error',
      duration = level === 'critical' ? undefined : 5000, // Critical errors don't auto-dismiss
      persistent = false,
      actions = []
    } = options;

    const notification: ErrorNotification = {
      id: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title,
      message,
      level,
      timestamp: new Date(),
      duration,
      persistent,
      actions,
      source: options.source
    };

    // Remove oldest notification if at max capacity
    if (this.notifications.length >= this.maxNotifications) {
      this.removeOldestNotification();
    }

    this.notifications = [notification, ...this.notifications];
    this.notifyListeners();

    // Auto-dismiss if duration is specified
    if (duration && duration > 0) {
      setTimeout(() => {
        this.removeNotification(notification.id);
      }, duration);
    }

    // Persist if needed
    if (persistent) {
      this.savePersistentNotifications();
    }

    return notification.id;
  }

  // Remove a notification
  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notifyListeners();
    this.savePersistentNotifications();
  }

  // Clear all notifications
  clearAll(): void {
    this.notifications = [];
    this.notifyListeners();
    this.savePersistentNotifications();
  }

  // Clear non-persistent notifications
  clearNonPersistent(): void {
    this.notifications = this.notifications.filter(n => n.persistent);
    this.notifyListeners();
    this.savePersistentNotifications();
  }

  // Get current notifications
  getNotifications(): ErrorNotification[] {
    return [...this.notifications];
  }

  // Get next z-index for layering
  getNextZIndex(): number {
    return this.zIndexBase + this.notifications.length;
  }

  // Convenience methods for different error levels
  showError(title: string, message: string, options: Omit<ErrorNotificationOptions, 'level'> = {}): string {
    return this.addNotification(title, message, { ...options, level: 'error' });
  }

  showWarning(title: string, message: string, options: Omit<ErrorNotificationOptions, 'level'> = {}): string {
    return this.addNotification(title, message, { ...options, level: 'warning' });
  }

  showCritical(title: string, message: string, options: Omit<ErrorNotificationOptions, 'level'> = {}): string {
    return this.addNotification(title, message, { ...options, level: 'critical' });
  }

  showInfo(title: string, message: string, options: Omit<ErrorNotificationOptions, 'level'> = {}): string {
    return this.addNotification(title, message, { ...options, level: 'info' });
  }

  // Show API error with formatted message
  showApiError(error: any, defaultMessage: string = i18n.t('errorHandling.generic.message')): string {
    const message = this.extractErrorMessage(error, defaultMessage);
    return this.showError(i18n.t('errorHandling.apiError'), message, { duration: 8000 });
  }

  // Show validation errors
  showValidationErrors(errors: Record<string, string[]>): string {
    const errorMessages = Object.entries(errors)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join('\n');
    
    return this.showWarning(i18n.t('errorHandling.validationError'), errorMessages, { duration: 10000 });
  }

  // Private methods
  private notifyListeners(): void {
    const notifications = this.getNotifications();
    this.listeners.forEach(callback => callback(notifications));
  }

  private removeOldestNotification(): void {
    const nonPersistentIndex = this.notifications.findIndex(n => !n.persistent);
    if (nonPersistentIndex !== -1) {
      this.notifications.splice(nonPersistentIndex, 1);
    } else {
      this.notifications.pop();
    }
  }

  private extractErrorMessage(error: any, defaultMessage: string): string {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      return error.response.data.errors.map((e: any) => e.message).join(', ');
    }
    if (error.message) {
      return error.message;
    }
    return defaultMessage;
  }

  private savePersistentNotifications(): void {
    const persistent = this.notifications.filter(n => n.persistent);
    localStorage.setItem('error_notifications_persistent', JSON.stringify(persistent));
  }

  private loadPersistentNotifications(): void {
    try {
      const stored = localStorage.getItem('error_notifications_persistent');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.notifications = parsed.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
      }
    } catch (error) {
      console.warn('Failed to load persistent notifications:', error);
    }
  }

  private setupVisibilityHandling(): void {
    // Clear non-persistent notifications on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.clearNonPersistent();
      }
    });

    // Clear notifications on beforeunload (except persistent ones)
    window.addEventListener('beforeunload', () => {
      this.clearNonPersistent();
    });
  }
}

export const errorNotificationManager = ErrorNotificationManager.getInstance();
export default ErrorNotificationManager;
